import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import {
  BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation, useSearchParams,
} from 'react-router';
import { Global } from '@emotion/react';
import {
  LayoutContainer, Sidebar, Wordmark, NavList, NavItem, StyledNavLink,
  NavUser, NavUserEmail, LayoutMain,
  ModalOverlay, ModalDialog, ModalTitle, ModalActions, FormGroup, Input, PrimaryButton, SecondaryButton,
} from './dashboard/styled-components';
import { ToastHost } from './dashboard/components';
import { showToast, useFetchData } from './dashboard/hooks';
import { canViewMonitor } from './dashboard/monitor-access';
import { cssVars } from './dashboard/theme';
import { useAsyncEffect } from './common/utils';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from './common/utils';
import {
  LayoutDashboard, Key, Phone, MessageSquare, Megaphone, Users, Settings, LogOut, Link2, Radio, Rocket, CreditCard, BookOpen, ChevronDown, Check, ShieldCheck, Plus, Monitor,
} from 'lucide-react';

// Pages
import { SignInPageComponent } from './dashboard/pages/sign-in';
import { HomePage } from './dashboard/pages/home';
import { ApiKeysPage } from './dashboard/pages/api-keys';
import { PhoneNumbersPage, WebRTCCodesPage, SipCodesPage } from './dashboard/pages/phone-numbers';
import { ConversationsPage, ConversationDetailPage } from './dashboard/pages/conversations';
import { CampaignsPage, CampaignDetailPage } from './dashboard/pages/campaigns';
import { CampaignUploadPage } from './dashboard/pages/campaign-upload';
import { TeamPage } from './dashboard/pages/team';
import { SettingsPage } from './dashboard/pages/settings';
import { BillingPage } from './dashboard/pages/billing';
import { BillingSetupPage } from './dashboard/pages/billing-setup';
import { BillingSetupCompletePage } from './dashboard/pages/billing-setup-complete';
import { ComplianceOnboardingPage } from './dashboard/pages/compliance-onboarding';
import { CliCallbackPage } from './dashboard/pages/cli-callback';
import { DeploymentsPage } from './dashboard/pages/deployments';
import { CompliancePage } from './dashboard/pages/compliance';
import { NotFoundPage } from './dashboard/pages/not-found';
import { OptInPage } from './dashboard/pages/opt-in';

// ---------------------------------------------------------------------------
// Auth / me
// ---------------------------------------------------------------------------

interface MeData {
  user_id: string;
  user_email: string;
  org_id: string;
  org_name: string;
  role: 'admin' | 'developer' | 'viewer';
  billing_setup_required: boolean;
  compliance_onboarding_required: boolean;
  can_create_extra_orgs: boolean;
}

async function fetchMe(): Promise<MeData | null> {
  try {
    const res = await fetch('/dashboard/api/me');
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const MeContext = React.createContext<MeData | null>(null);
function useMe() { return React.useContext(MeContext); }

// ---------------------------------------------------------------------------
// RequireAuth
// ---------------------------------------------------------------------------

function RequireAuth() {
  const [status, setStatus] = useState<'loading' | 'ok' | 'unauth'>('loading');
  const [me, setMe] = useState<MeData | null>(null);

  useAsyncEffect(async (isActive) => {
    const data = await fetchMe();
    if (!isActive()) return;
    if (data) { setMe(data); setStatus('ok'); }
    else setStatus('unauth');
  }, []);

  if (status === 'unauth') return <Navigate to='/sign-in' replace />;
  if (status === 'ok' && me?.billing_setup_required) return <Navigate to='/billing-setup' replace />;
  if (status === 'ok' && me?.compliance_onboarding_required) return <Navigate to='/compliance-onboarding' replace />;

  if (status === 'loading') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', color: 'var(--text-muted)', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }

  return (
    <MeContext.Provider value={me}>
      <Outlet />
    </MeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Nav items
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  { to: '/',              label: 'Home',          Icon: LayoutDashboard, end: true,  minRole: null },
  { to: '/api-keys',      label: 'API Keys',       Icon: Key,             end: false, minRole: null },
  { to: '/phone-numbers', label: 'Phone Numbers',  Icon: Phone,           end: false, minRole: null },
  { to: '/sip',           label: 'SIP',            Icon: Link2,           end: false, minRole: null },
  { to: '/webrtc',        label: 'WebRTC',         Icon: Radio,           end: false, minRole: null },
  { to: '/conversations', label: 'Conversations',  Icon: MessageSquare,   end: false, minRole: null },
  { to: '/campaigns',     label: 'Campaigns',      Icon: Megaphone,       end: false, minRole: null },
  { to: '/deployments',   label: 'Deployments',    Icon: Rocket,          end: false, minRole: null },
  { to: '/compliance',   label: 'Compliance',     Icon: ShieldCheck,     end: false, minRole: null },
  { to: '/team',          label: 'Team',           Icon: Users,           end: false, minRole: 'admin' as const },
  { to: '/settings',      label: 'Settings',       Icon: Settings,        end: false, minRole: null },
  { to: '/billing',       label: 'Billing',        Icon: CreditCard,      end: false, minRole: 'admin' as const },
];

const ROLE_RANK = { admin: 0, developer: 1, viewer: 2 } as const;

function canAccess(minRole: 'admin' | null, userRole: string): boolean {
  if (!minRole) return true;
  return ROLE_RANK[userRole as keyof typeof ROLE_RANK] <= ROLE_RANK[minRole];
}

// ---------------------------------------------------------------------------
// Sign-out button
// ---------------------------------------------------------------------------

function SignOutBtn() {
  const navigate = useNavigate();

  async function handleSignOut() {
    try {
      await fetchOrThrow('/dashboard/api/sign-out', {
        method: 'POST',
        headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
    } catch { /* ignore */ }
    navigate('/sign-in', { replace: true });
  }

  return (
    <button
      type="button"
      onClick={handleSignOut}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--text-muted)', fontSize: 13, padding: '6px 4px',
        borderRadius: 6, transition: 'color 120ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent-red)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
    >
      <LogOut size={15} />
      Sign Out
    </button>
  );
}

// ---------------------------------------------------------------------------
// Org switcher
// ---------------------------------------------------------------------------

interface OrgEntry { org_id: string; org_name: string; role: string; }

function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetchOrThrow('/dashboard/api/me/orgs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ name }),
      });
      const { org_id } = await res.json();
      await fetchOrThrow('/dashboard/api/me/switch-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ org_id }),
      });
      window.location.replace('/dashboard/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create organization');
      setLoading(false);
    }
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>New Organization</ModalTitle>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Corp"
              autoFocus
              required
            />
          </FormGroup>
          {error && <div style={{ color: 'var(--accent-red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <ModalActions>
            <SecondaryButton type="button" onClick={onClose} disabled={loading}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading || !name}>
              {loading ? 'Creating…' : 'Create Organization'}
            </PrimaryButton>
          </ModalActions>
        </form>
      </ModalDialog>
    </ModalOverlay>
  );
}

function OrgSwitcher({ currentOrgId, canCreateExtraOrgs }: { currentOrgId: string; canCreateExtraOrgs: boolean }) {
  const { data: orgs } = useFetchData<OrgEntry[]>('/dashboard/api/me/orgs', []);
  const [open, setOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!orgs || (orgs.length <= 1 && !canCreateExtraOrgs)) return null;

  const current = orgs.find((o) => o.org_id === currentOrgId);

  async function handleSwitch(orgId: string) {
    setOpen(false);
    if (orgId === currentOrgId) return;
    try {
      await fetchOrThrow('/dashboard/api/me/switch-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [CSRF_HEADER_NAME]: await getCSRFToken() },
        body: JSON.stringify({ org_id: orgId }),
      });
      // Stay where we are: switching is usually to reach a link shared from
      // another org. Full reload, not a router nav — every cached fetch in the
      // tree still holds the previous org's data.
      window.location.replace(window.location.pathname + window.location.search + window.location.hash);
    } catch {
      showToast('Failed to switch organization', 'error');
    }
  }

  return (
    <>
      {showCreateModal && createPortal(
        <CreateOrgModal onClose={() => setShowCreateModal(false)} />,
        document.body
      )}
      <div ref={ref} style={{ position: 'relative', padding: '0 8px 8px' }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', background: 'var(--bg-surface)', border: '1px solid var(--bg-border)',
            borderRadius: 6, padding: '7px 10px', cursor: 'pointer',
            color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, gap: 6,
            transition: 'border-color 150ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent-cyan)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bg-border)'; }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {current?.org_name ?? '…'}
          </span>
          <ChevronDown size={12} style={{ flexShrink: 0, opacity: 0.6, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease' }} />
        </button>

        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% - 4px)', left: 8, right: 8,
            background: 'var(--bg-surface)', border: '1px solid var(--bg-border)',
            borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 100,
            overflow: 'hidden',
          }}>
            {orgs.map((org) => (
              <button
                type="button"
                key={org.org_id}
                onClick={() => handleSwitch(org.org_id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '9px 12px', fontSize: 12, fontWeight: 500, gap: 8,
                  color: org.org_id === currentOrgId ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                  transition: 'background 120ms ease, color 120ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; if (org.org_id !== currentOrgId) e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = org.org_id === currentOrgId ? 'var(--accent-cyan)' : 'var(--text-secondary)'; }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.org_name}</span>
                {org.org_id === currentOrgId && <Check size={12} style={{ flexShrink: 0 }} />}
              </button>
            ))}
            {canCreateExtraOrgs && (
              <button
                type="button"
                onClick={() => { setOpen(false); setShowCreateModal(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', background: 'none', border: 'none', borderTop: '1px solid var(--bg-border)',
                  cursor: 'pointer', padding: '9px 12px', fontSize: 12, fontWeight: 500,
                  color: 'var(--text-muted)', transition: 'background 120ms ease, color 120ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                <Plus size={12} style={{ flexShrink: 0 }} />
                New organization
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main layout
// ---------------------------------------------------------------------------

function MainLayout() {
  const me = useMe();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const joined = searchParams.get('joined');
    if (joined) {
      showToast(`You've joined ${joined}`, 'success');
      setSearchParams((prev) => { prev.delete('joined'); return prev; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <LayoutContainer>
      <Sidebar>
        <Wordmark>
          <img src="/static/guava-logo.png" alt="Guava" style={{ height: 80, display: 'block', filter: 'grayscale(1) brightness(0.75)', margin: '-16px 0' }} />
        </Wordmark>
        {me && <OrgSwitcher currentOrgId={me.org_id} canCreateExtraOrgs={me.can_create_extra_orgs} />}

        <NavList>
          {NAV_ITEMS.map(({ to, label, Icon, end, minRole }) => {
            if (!canAccess(minRole, me?.role ?? 'viewer')) return null;
            return (
              <NavItem key={to}>
                <StyledNavLink to={to} end={end}>
                  <Icon size={15} />
                  {label}
                </StyledNavLink>
              </NavItem>
            );
          })}
          <NavItem>
            <a
              href='https://goguava.ai/docs'
              target='_blank'
              rel='noreferrer'
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 6, fontSize: 11,
                fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
                color: 'var(--text-secondary)', textDecoration: 'none',
                transition: 'background 150ms ease, color 150ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              <BookOpen size={15} />
              Docs
            </a>
          </NavItem>
          {/* Internal-only appendage: opens the Monitor app. Same-tab (a plain
              link, so right-click → open in new tab still works). Gated on the
              email domain; Monitor enforces the real can_view_monitor check. */}
          {canViewMonitor(me?.user_email) && (
            <NavItem>
              <a
                href='/monitor/'
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 6, fontSize: 11,
                  fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: 'var(--text-secondary)', textDecoration: 'none',
                  transition: 'background 150ms ease, color 150ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                <Monitor size={15} />
                Monitor
              </a>
            </NavItem>
          )}
        </NavList>

        <NavUser>
          {me && <NavUserEmail>{me.user_email}</NavUserEmail>}
          <SignOutBtn />
        </NavUser>
      </Sidebar>

      <LayoutMain>
        <div key={location.pathname} style={{ animation: 'pageIn 200ms ease', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </div>
      </LayoutMain>
    </LayoutContainer>
  );
}

// ---------------------------------------------------------------------------
// TeamPage wrapper (passes currentUserId)
// ---------------------------------------------------------------------------

function TeamPageWrapper() {
  const me = useMe();
  return <TeamPage currentUserId={me?.user_id} />;
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

function App() {
  return (
    <BrowserRouter basename='/dashboard'>
      <Global styles={cssVars} />
      <ToastHost />
      <Routes>
        <Route path='/sign-in' element={<SignInPageComponent />} />
        <Route path='/join' element={<SignInPageComponent />} />
        <Route path='/billing-setup' element={<BillingSetupPage />} />
        <Route path='/billing-setup/complete' element={<BillingSetupCompletePage />} />
        <Route path='/compliance-onboarding' element={<ComplianceOnboardingPage />} />
        <Route path='/cli-callback' element={<CliCallbackPage />} />
        <Route path='/opt-in' element={<OptInPage />} />

        <Route element={<RequireAuth />}>
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />
            <Route path='api-keys' element={<ApiKeysPage />} />
            <Route path='phone-numbers' element={<PhoneNumbersPage />} />
            <Route path='sip' element={<SipCodesPage />} />
            <Route path='webrtc' element={<WebRTCCodesPage />} />
            <Route path='conversations' element={<ConversationsPage />} />
            <Route path='conversations/:callId' element={<ConversationDetailPage />} />
            <Route path='campaigns' element={<CampaignsPage />} />
            <Route path='campaigns/:campaignId' element={<CampaignDetailPage />} />
            <Route path='campaigns/:campaignId/upload' element={<CampaignUploadPage />} />
            <Route path='deployments' element={<DeploymentsPage />} />
            <Route path='compliance' element={<CompliancePage />} />
            <Route path='team' element={<TeamPageWrapper />} />
            <Route path='settings' element={<SettingsPage />} />
            <Route path='billing' element={<BillingPage />} />
          </Route>
        </Route>

        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById('mount')!);
root.render(<App />);
